import { supabase } from '../supabaseClient';
import type { SocialPlatform } from '../../components/SocialConnectButton';

export type GoogleReviewChannelTag = {
  platform: SocialPlatform;
  connectionId: string;
  label: string;
  usedAt: string;
};

/** Load which social channels each Google review has already been staged/posted to. */
export async function listGoogleReviewSocialUsage(
  businessId: string,
  reviewIds?: string[],
): Promise<Map<string, GoogleReviewChannelTag[]>> {
  const map = new Map<string, GoogleReviewChannelTag[]>();
  if (!businessId) return map;

  let query = supabase
    .from('google_review_social_usage')
    .select(
      'review_id, connection_id, platform, used_at, social_media_connections(platform, display_name, username)',
    )
    .eq('business_id', businessId)
    .order('used_at', { ascending: false });

  if (reviewIds && reviewIds.length > 0) {
    query = query.in('review_id', reviewIds);
  }

  const { data, error } = await query;
  if (error) {
    console.warn('Failed to load Google review social usage:', error);
    return map;
  }

  for (const row of data || []) {
    const reviewId = String(row.review_id || '');
    if (!reviewId) continue;
    const conn = row.social_media_connections as
      | { platform?: string; display_name?: string | null; username?: string | null }
      | { platform?: string; display_name?: string | null; username?: string | null }[]
      | null;
    const c = Array.isArray(conn) ? conn[0] : conn;
    const platform = (row.platform || c?.platform || 'instagram') as SocialPlatform;
    const name =
      (c?.display_name || c?.username || '').toString().trim() ||
      (platform === 'facebook' ? 'Facebook' : platform === 'linkedin' ? 'LinkedIn' : 'Instagram');
    const tag: GoogleReviewChannelTag = {
      platform,
      connectionId: String(row.connection_id),
      label:
        platform === 'facebook'
          ? `Facebook · ${name}`
          : platform === 'linkedin'
            ? `LinkedIn · ${name}`
            : `Instagram · ${name}`,
      usedAt: String(row.used_at || ''),
    };
    const list = map.get(reviewId) || [];
    list.push(tag);
    map.set(reviewId, list);
  }

  return map;
}
